import React, { useEffect, useState } from "react";
import { FaLock } from "react-icons/fa";
import { useAccount } from "wagmi";
import { useX402Pay } from "../../hooks/useX402Pay";
import { useDirectPay } from "../../hooks/useDirectPay";
import { getTxExplorerUrl } from "../../helper/x402/chains";
import { ConnectWalletCTA } from "./ConnectWalletCTA";
import { ConnectedWalletRow } from "./ConnectedWalletRow";
import { MethodSwitcher } from "./MethodSwitcher";
import { PayStatusStrip } from "./PayStatusStrip";
import { ReceiptCard } from "./ReceiptCard";

/**
 * Single-flow checkout: replaces PaymentMethodTabs +
 * WalletPanel + DirectPanel. Renders one of four visual states:
 *
 *   needs-connect → Connect CTA
 *   ready         → ConnectedWalletRow + Pay button + switcher
 *   working       → same as ready, button disabled + status strip
 *   success       → <ReceiptCard /> replaces the panel
 *
 * We ALWAYS call both useX402Pay and useDirectPay so React's
 * rules-of-hooks are satisfied; we just read from whichever one
 * matches activeMethod. Both hooks start at 'idle' and stay
 * there until their respective pay() / payNow() is called, so
 * there is no cross-talk.
 *
 * When the user switches methods, we call reset() on the
 * previously-active hook so a lingering "signing…" flag from
 * x402 doesn't flash when jumping to direct.
 */
export function CheckoutPanel({ link }) {
  const { isConnected } = useAccount();

  // activeMethod defaults to link.recommendedMethod once /info
  // resolves. If the recommended method is missing from the
  // paymentMethods array (edge case: backend flipped a flag),
  // fall back to the first available method.
  const [activeMethod, setActiveMethod] = useState(null);
  useEffect(() => {
    if (!link?.paymentMethods?.length) return;
    const recommended = link.recommendedMethod;
    const available = link.paymentMethods;
    const next = available.includes(recommended) ? recommended : available[0];
    setActiveMethod((prev) => (prev == null ? next : prev));
  }, [link]);

  const x402 = useX402Pay(link);
  const direct = useDirectPay(link);

  // Method switch: reset the hook we're leaving before flipping
  // activeMethod so any mid-flow state is cleared.
  const handleSwitch = (next) => {
    if (activeMethod === "x402") x402.reset();
    else if (activeMethod === "direct") direct.reset();
    setActiveMethod(next);
  };

  // Change-wallet: reset BOTH hooks — we don't know which one
  // might have partial state.
  const handleChangeWallet = () => {
    x402.reset();
    direct.reset();
  };

  // No usable payment methods on this link — safety net; the
  // backend normally doesn't surface these.
  if (!link?.paymentMethods?.length) {
    return (
      <div className="pay-checkout">
        <h3 className="pay-checkout__heading">
          This link isn't accepting payments
        </h3>
        <p className="pay-panel__subheading">
          The merchant hasn't configured a supported payment method. Contact
          them for an alternate link.
        </p>
      </div>
    );
  }

  // Success: ReceiptCard replaces the whole panel. Pick the
  // receipt from whichever hook produced it.
  if (x402.status === "success" && x402.receipt) {
    return <ReceiptCard receipt={x402.receipt} link={link} />;
  }
  if (direct.phase === "success" && direct.receipt) {
    return (
      <ReceiptCard receipt={{ receipt: direct.receipt }} link={link} method="direct" />
    );
  }

  if (!isConnected) {
    const subcopy =
      activeMethod === "direct"
        ? `Pay with direct transfer on ${link.network}. Your wallet covers the on-chain gas fee.`
        : `Pay gasless with ${link.tokenSymbol} on ${link.network}. You'll sign a single authorization — no gas, no approval transaction.`;
    return (
      <div className="pay-checkout">
        <h3 className="pay-checkout__heading">Connect your wallet</h3>
        <p className="pay-panel__subheading">{subcopy}</p>
        <ConnectWalletCTA />
      </div>
    );
  }

  // Connected + pending/idle/error. Normalize the hook state
  // into PayStatusStrip's phase enum.
  const isX402 = activeMethod === "x402";
  const raw = isX402 ? x402.status : direct.phase;
  const phase = raw === "success" ? "idle" : raw || "idle";
  const error = isX402 ? x402.error : direct.error;
  const isWorking =
    phase !== "idle" && phase !== "error";

  const explorerUrl =
    !isX402 && direct.txHash
      ? getTxExplorerUrl(link.chainId, direct.txHash)
      : null;

  let buttonLabel;
  if (phase === "idle" || phase === "error") {
    buttonLabel = `Pay ${link.amountDisplay}`;
  } else if (phase === "switching") {
    buttonLabel = "Switching network…";
  } else if (phase === "signing") {
    buttonLabel = "Sign in wallet…";
  } else if (phase === "submitting") {
    buttonLabel = "Settling on-chain…";
  } else if (phase === "sending") {
    buttonLabel = "Step 1 of 2 — Confirm in wallet…";
  } else if (phase === "attesting") {
    buttonLabel = "Step 2 of 2 — Sign confirmation…";
  } else if (phase === "confirming") {
    buttonLabel = direct.requiredConfirmations
      ? `Confirming (${direct.confirmations}/${direct.requiredConfirmations})…`
      : "Waiting for confirmations…";
  } else if (phase === "recording") {
    buttonLabel = "Recording payment…";
  } else {
    buttonLabel = `Pay ${link.amountDisplay}`;
  }

  const handlePay = () => {
    if (phase === "error") {
      if (isX402) x402.reset();
      else direct.reset();
    }
    if (isX402) x402.payNow();
    else direct.pay();
  };

  return (
    <div className="pay-checkout">
      <h3 className="pay-checkout__heading">Review your payment</h3>
      <ConnectedWalletRow onChange={handleChangeWallet} />

      <button
        type="button"
        className="pay-button"
        onClick={handlePay}
        disabled={isWorking}
      >
        <FaLock size={14} />
        {buttonLabel}
      </button>

      <PayStatusStrip
        phase={phase}
        error={error}
        network={link.network}
        confirmations={direct.confirmations}
        requiredConfirmations={direct.requiredConfirmations}
        explorerUrl={explorerUrl}
      />

      <MethodSwitcher
        link={link}
        activeMethod={activeMethod}
        onSwitch={handleSwitch}
      />
    </div>
  );
}
